import { memo, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Transition } from '@headlessui/react';
import { CustomizerMenuType } from './constants';
import ClipCustomizerCaptionsPopup from './ClipCustomizerCaptionsPopup';
import ClipCustomizerTemplatesPopup from './ClipCustomizerTemplatesPopup';
import ClipCustomizerBackgroundPopup from './ClipCustomizerBackgroundPopup';
import ClipCustomizerSpeakersPopup from './Speakers/ClipCustomizerSpeakersPopup';
import ClipCustomizerImagesPopup from './ClipCustomizerImagesPopup';
import ClipCustomizerVideoPopup from './ClipCustomizerVideoPopup';
import ClipCustomizerAudioPopup from './ClipCustomizerAudioPopup';
import { timelineActionsStore } from '../ClipNewTimeline/store/timelineActions';
import useClipCustomizerWarnings from './useClipCustomizerWarnings';
import ClipCustomizerLayoutPopupV2 from '@/Pages/Clip/ClipPlayer/ClipCustomizerV2/Layout/ClipCustomizerLayoutPopup';
import ActionButtons, { ActionButton } from '@/components/atoms/ActionButtons/ActionButtons';
import DynamicPopup from '@/components/atoms/DynamicPopup/DynamicPopup';
import { isAnyDialogOpened } from '@/Pages/Sessions/uiComponents/ContentDialog/utils';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';
import CaptionLanguageSelectorPopup from '@/Pages/Clip/CaptionTranslation/CaptionLanguageSelectorPopup';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import useTranslationState from '@/hooks/useTranslationState';
import { LanguageOption } from '@/player-config/src/types/clip-types';
import { UnsupportedLanguageWarningModal } from '@/components/molecules/UnsupportedLanguageWarningModal/UnsupportedLanguageWarningModal';
import {
  areSpeakerLayoutsEnabled,
  isLayoutsWithVisionDataEnabled,
  isSplitsFeatureEnabled,
  isSplitsInTimelineEnabled
} from '@/stores/clip';
import { core } from '@/stores/core';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { useRestrictedFeature } from '@/hooks/useGlobalRestrictionDialog';
import { aiEditsStore } from '@/stores/aiEditsStore';
import AIEditsTooltip, {
  hasDismissedAIEditsTooltip,
  markAIEditsTooltipAsDismissed
} from '@/components/organisms/AIEditsTooltip/AIEditsTooltip';
import { isSpeakerLayout } from '@/player-config/src/utils/layout';
import { getClipDurationInSeconds } from '@/player-config/src/utils/clipUtils';

function ClipCustomizerMenu({
  isFullRecordingEdit = false,
  onTimeUpdate,
  useNewRecordingsPlayer
}: {
  isFullRecordingEdit?: boolean;
  onTimeUpdate: (time: number, shouldPlay?: boolean) => void;
  useNewRecordingsPlayer?: boolean;
}) {
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);
  const { isAddingImage, isAddingVideo, isAddingAudio } = useSyncExternalStore(
    timelineActionsStore.subscribe,
    timelineActionsStore.getSnapshot
  );
  const { clipId, clipData } = useClipsContext();
  const clipCustomizerWarnings = useClipCustomizerWarnings();

  const {
    isTranslationProcessing,
    allowCaptionTranslation,
    onCaptionTranslationConfirm,
    handleCaptionTranslationRestricted,
    isOriginalLanguageSupportedForTranslation,
    checkLanguageAndExecute,
    isWarningModalOpen,
    closeWarningModal
  } = useTranslationState(clipData);

  const { handleAction } = useRestrictedFeature(
    'captionTranslation',
    allowCaptionTranslation,
    () => checkLanguageAndExecute(() => openPopupMenu(CustomizerMenuType.Translation)),
    {
      reason: 'plan',
      onSecondaryAction: handleCaptionTranslationRestricted
    }
  );

  const [isMenuPopupOpened, setIsMenuPopupOpened] = useState<CustomizerMenuType | null>(null);
  const [showAIEditsTooltip, setShowAIEditsTooltip] = useState(false);

  // Create refs for each button
  const promptOverrideRef = useRef<HTMLButtonElement>(null);
  const translationRef = useRef<HTMLButtonElement>(null);
  const aiRef = useRef<HTMLButtonElement>(null);
  const speakersRef = useRef<HTMLButtonElement>(null);
  const templatesRef = useRef<HTMLButtonElement>(null);
  const layoutRef = useRef<HTMLButtonElement>(null);
  const captionsRef = useRef<HTMLButtonElement>(null);
  const backgroundRef = useRef<HTMLButtonElement>(null);
  const imagesRef = useRef<HTMLButtonElement>(null);
  const videosRef = useRef<HTMLButtonElement>(null);
  const audioRef = useRef<HTMLButtonElement>(null);

  const buttonRefs = useMemo(
    () => ({
      [CustomizerMenuType.PromptOverride]: promptOverrideRef,
      [CustomizerMenuType.Translation]: translationRef,
      [CustomizerMenuType.AI]: aiRef,
      [CustomizerMenuType.Speakers]: speakersRef,
      [CustomizerMenuType.Templates]: templatesRef,
      [CustomizerMenuType.Layout]: layoutRef,
      [CustomizerMenuType.Captions]: captionsRef,
      [CustomizerMenuType.Background]: backgroundRef,
      [CustomizerMenuType.Images]: imagesRef,
      [CustomizerMenuType.Videos]: videosRef,
      [CustomizerMenuType.Audio]: audioRef
    }),
    []
  );
  const { isAIEditsSidebarOpen } = useSyncExternalStore(aiEditsStore.subscribe, aiEditsStore.getSnapshot);
  const isAIAgentSidebarOpen = isAIEditsSidebarOpen;

  const closePopup = useCallback(() => {
    setIsMenuPopupOpened(null);
  }, []);

  const openPopupMenu = useCallback(
    (menuType: CustomizerMenuType) => {
      isMenuPopupOpened !== menuType ? setIsMenuPopupOpened(menuType) : closePopup();
    },
    [closePopup, isMenuPopupOpened]
  );

  const isAudioOverlayEnabled = useMemo(() => {
    return featureFlags[FeatureFlagKeys.Use_CL_Timeline_Add_Menu];
  }, [featureFlags]);

  const { isAIEditsHidden, isAiEditsDisabled, AIEditsDisabledReason } = useMemo(() => {
    const isNotAudioContent = coreStore.content?.media_type !== 'AUDIO';
    const splitsInTimelineEnabled = isSplitsFeatureEnabled(featureFlags, clipData);
    const shouldShowAIEdits = isNotAudioContent && (splitsInTimelineEnabled || !isSplitsInTimelineEnabled(clipData));

    const clipDuration = getClipDurationInSeconds(clipData);
    const isClipDurationWithinAIEditsLimit = clipDuration && clipDuration <= 300;

    const isSpeakerAnalysisAvailableForClip = areSpeakerLayoutsEnabled(clipData);
    const isAIEditsEnabled = isClipDurationWithinAIEditsLimit && isSpeakerAnalysisAvailableForClip;

    let disabledReason: string | undefined;
    if (!isAIEditsEnabled) {
      if (!isClipDurationWithinAIEditsLimit) {
        disabledReason = 'AI Edits only work with shorter (<5 minutes) content for now.';
      } else if (!isSpeakerAnalysisAvailableForClip) {
        disabledReason = 'AI Edits would be available for uploads post complete processing.';
      }
    }

    return {
      isAIEditsHidden: !(featureFlags[FeatureFlagKeys.Use_CL_AI_Edits_Beta] && shouldShowAIEdits),
      isAiEditsDisabled: !isAIEditsEnabled,
      AIEditsDisabledReason: disabledReason
    };
  }, [featureFlags, clipData, coreStore.content?.media_type]);

  const isSplitsTranslationEnabled = useMemo(() => {
    return featureFlags[FeatureFlagKeys.Use_CL_Splits_Translations];
  }, [featureFlags]);

  const isCaptionTranslationEnabledForClip = useMemo(() => {
    return isSplitsTranslationEnabled || !isSplitsInTimelineEnabled(clipData);
  }, [clipData, isSplitsTranslationEnabled]);

  const { isCaptionTranslationEnabled } = useMemo(() => {
    return {
      isCaptionTranslationEnabled: !isFullRecordingEdit && isCaptionTranslationEnabledForClip
    };
  }, [isFullRecordingEdit, isCaptionTranslationEnabledForClip]);

  const isAudioControlsEnabled = useMemo(() => {
    return featureFlags[FeatureFlagKeys.Use_CL_Audio_Controls];
  }, [featureFlags]);

  const shouldTemplatesBeHidden = false;

  const handleAiEditsButtonClick = useCallback(() => {
    window.analytics?.track('ContentLabEvent', {
      name: 'AIEditsSidebarOpened',
      eventTrigger: 'ManualClick',
      clipId: clipId,
      mediaType: coreStore.content?.media_type,
      hadTooltip: showAIEditsTooltip
    });

    markAIEditsTooltipAsDismissed();
    setShowAIEditsTooltip(false);
    aiEditsStore.setIsAIEditsSidebarOpen(true, 'ManualClick');
  }, [clipId, coreStore.content, showAIEditsTooltip]);

  const buttons: ActionButton[] = useMemo(() => {
    return [
      {
        action: () => handleAiEditsButtonClick(),
        icon: 'IconWand',
        label: 'AI Edits',
        trackingId: 'ai-magic-button',
        disabled: isAiEditsDisabled,
        isActive: isAIAgentSidebarOpen || isMenuPopupOpened === CustomizerMenuType.AI,
        hidden: isAIEditsHidden,
        isLoading: false,
        tooltip: AIEditsDisabledReason,
        buttonRef: buttonRefs[CustomizerMenuType.AI],
        isBetaFeature: true,
        showWarning: clipCustomizerWarnings[CustomizerMenuType.AI].showWarning,
        warnings: clipCustomizerWarnings[CustomizerMenuType.AI].warnings
      },
      {
        action: () => handleAction(),
        icon: 'IconLanguage',
        label: isTranslationProcessing ? 'Translating' : 'Translate',
        trackingId: 'caption-translation-button',
        disabled: isTranslationProcessing,
        isActive: isMenuPopupOpened === CustomizerMenuType.Translation,
        hidden: !isCaptionTranslationEnabled,
        isLoading: isTranslationProcessing,
        isLocked: !allowCaptionTranslation,
        buttonRef: buttonRefs[CustomizerMenuType.Translation],
        showWarning:
          clipCustomizerWarnings[CustomizerMenuType.Translation].showWarning ||
          (allowCaptionTranslation && !isOriginalLanguageSupportedForTranslation),
        warnings: clipCustomizerWarnings[CustomizerMenuType.Translation].warnings
      },
      {
        action: () => openPopupMenu(CustomizerMenuType.Templates),
        icon: 'IconTemplate',
        label: 'Templates',
        trackingId: 'edit-clip-templates-button',
        disabled: false,
        isActive: isMenuPopupOpened === CustomizerMenuType.Templates,
        hidden: shouldTemplatesBeHidden,
        buttonRef: buttonRefs[CustomizerMenuType.Templates],
        showWarning: clipCustomizerWarnings[CustomizerMenuType.Templates].showWarning,
        warnings: clipCustomizerWarnings[CustomizerMenuType.Templates].warnings
      },
      {
        action: () => openPopupMenu(CustomizerMenuType.Layout),
        icon: 'IconLayoutDashboard',
        label: 'Layout',
        trackingId: 'edit-clip-layout-button',
        disabled: false,
        isActive: isMenuPopupOpened === CustomizerMenuType.Layout,
        buttonRef: buttonRefs[CustomizerMenuType.Layout],
        showWarning: clipCustomizerWarnings[CustomizerMenuType.Layout].showWarning,
        warnings: clipCustomizerWarnings[CustomizerMenuType.Layout].warnings
      },
      {
        action: () => openPopupMenu(CustomizerMenuType.Captions),
        icon: 'IconAlignBoxCenterBottom',
        label: 'Captions',
        trackingId: 'edit-clip-captions-button',
        disabled: false,
        isActive: isMenuPopupOpened === CustomizerMenuType.Captions,
        buttonRef: buttonRefs[CustomizerMenuType.Captions],
        showWarning: clipCustomizerWarnings[CustomizerMenuType.Captions].showWarning,
        warnings: clipCustomizerWarnings[CustomizerMenuType.Captions].warnings
      },
      {
        action: () => openPopupMenu(CustomizerMenuType.Background),
        icon: 'IconBackground',
        label: 'Background',
        trackingId: 'edit-clip-background-button',
        disabled: false,
        isActive: isMenuPopupOpened === CustomizerMenuType.Background,
        buttonRef: buttonRefs[CustomizerMenuType.Background],
        showWarning: clipCustomizerWarnings[CustomizerMenuType.Background].showWarning,
        warnings: clipCustomizerWarnings[CustomizerMenuType.Background].warnings
      },
      {
        action: () => openPopupMenu(CustomizerMenuType.Images),
        icon: 'IconPhoto',
        label: 'Images',
        trackingId: 'edit-clip-images-button',
        disabled: false,
        isActive: isMenuPopupOpened === CustomizerMenuType.Images,
        hidden: false,
        isLoading: isAddingImage,
        buttonRef: buttonRefs[CustomizerMenuType.Images],
        showWarning: clipCustomizerWarnings[CustomizerMenuType.Images].showWarning,
        warnings: clipCustomizerWarnings[CustomizerMenuType.Images].warnings
      },
      {
        action: () => openPopupMenu(CustomizerMenuType.Videos),
        icon: 'IconMovie',
        label: 'Videos',
        trackingId: 'edit-clip-videos-button',
        disabled: false,
        isActive: isMenuPopupOpened === CustomizerMenuType.Videos,
        hidden: false,
        isLoading: isAddingVideo,
        buttonRef: buttonRefs[CustomizerMenuType.Videos],
        showWarning: clipCustomizerWarnings[CustomizerMenuType.Videos].showWarning,
        warnings: clipCustomizerWarnings[CustomizerMenuType.Videos].warnings
      },
      {
        action: () => openPopupMenu(CustomizerMenuType.Audio),
        icon: isAudioControlsEnabled ? 'IconMusic' : 'IconHeadphones',
        label: isAudioControlsEnabled ? 'Music' : 'Audio',
        trackingId: 'edit-clip-audio-button',
        disabled: false,
        isActive: isMenuPopupOpened === CustomizerMenuType.Audio,
        hidden: !isAudioOverlayEnabled,
        isLoading: isAddingAudio,
        buttonRef: buttonRefs[CustomizerMenuType.Audio],
        showWarning: clipCustomizerWarnings[CustomizerMenuType.Audio].showWarning,
        warnings: clipCustomizerWarnings[CustomizerMenuType.Audio].warnings
      }
    ];
  }, [
    isMenuPopupOpened,
    isAudioControlsEnabled,
    isAIAgentSidebarOpen,
    AIEditsDisabledReason,
    isAiEditsDisabled,
    isAIEditsHidden,
    isTranslationProcessing,
    isCaptionTranslationEnabled,
    allowCaptionTranslation,
    isOriginalLanguageSupportedForTranslation,
    shouldTemplatesBeHidden,
    isAddingImage,
    isAddingVideo,
    isAudioOverlayEnabled,
    isAddingAudio,
    openPopupMenu,
    handleAiEditsButtonClick,
    handleAction,
    buttonRefs,
    clipCustomizerWarnings
  ]);

  const popupMenu = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMenuPopupOpened) {
      return;
    }
    function handleOutsideClick(e: MouseEvent) {
      if (isAnyDialogOpened()) {
        return;
      }
      if (!popupMenu.current?.contains(e.target as Node)) {
        closePopup();
      }
    }
    document.addEventListener('pointerdown', handleOutsideClick);
    return () => {
      document.removeEventListener('pointerdown', handleOutsideClick);
    };
  }, [isMenuPopupOpened, closePopup]);

  useEffect(() => {
    const handleOpenCaptionsGallery = () => {
      openPopupMenu(CustomizerMenuType.Captions);
    };

    const eventListener = EventBus.on(CustomEvents.OpenCaptionsGallery, handleOpenCaptionsGallery);
    return () => {
      EventBus.off(CustomEvents.OpenCaptionsGallery, eventListener);
    };
  }, [openPopupMenu]);

  useEffect(() => {
    const handleOpenAudioOverlayLibraryModal = () => {
      openPopupMenu(CustomizerMenuType.Audio);
    };
    const eventListener = EventBus.on(CustomEvents.OpenAudioOverlayLibraryModal, handleOpenAudioOverlayLibraryModal);
    return () => {
      EventBus.off(CustomEvents.OpenAudioOverlayLibraryModal, eventListener);
    };
  }, [openPopupMenu]);

  useEffect(() => {
    const handleOpenVideoOverlayLibraryModal = () => {
      openPopupMenu(CustomizerMenuType.Videos);
    };
    const eventListener = EventBus.on(CustomEvents.OpenVideoOverlayLibraryModal, handleOpenVideoOverlayLibraryModal);
    return () => {
      EventBus.off(CustomEvents.OpenVideoOverlayLibraryModal, eventListener);
    };
  }, [openPopupMenu]);

  // Show tooltip when AI edits are enabled and accessible, and user hasn't dismissed it
  useEffect(() => {
    const shouldShow =
      !isAIEditsHidden &&
      !isAiEditsDisabled &&
      !hasDismissedAIEditsTooltip() &&
      !isAIAgentSidebarOpen &&
      isMenuPopupOpened !== CustomizerMenuType.AI;

    setShowAIEditsTooltip(shouldShow);
  }, [isAIEditsHidden, isAiEditsDisabled, isAIAgentSidebarOpen, isMenuPopupOpened]);

  // Hide tooltip when AI edits sidebar opens or popup opens
  useEffect(() => {
    if (isAIAgentSidebarOpen || isMenuPopupOpened === CustomizerMenuType.AI) {
      markAIEditsTooltipAsDismissed();
      setShowAIEditsTooltip(false);
    }
  }, [isAIAgentSidebarOpen, isMenuPopupOpened]);

  // Track if speaker identification popup has been triggered to prevent re-triggering
  const hasTriggeredSpeakerIdentificationRef = useRef(false);

  // Auto-open speaker identification popup when conditions are met on page load
  useEffect(() => {
    // Skip if already triggered (even if closed, don't trigger again on layout change)
    if (hasTriggeredSpeakerIdentificationRef.current) {
      return;
    }

    // Check if feature flag is enabled
    if (!isLayoutsWithVisionDataEnabled(clipData)) {
      return;
    }

    // Check if the layout is a speaker layout
    // Give preference to scenes - if scenes exist and have length > 0, check scenes
    // Otherwise fall back to clip.asset_metadata.layout
    const clipLayout = clipData.asset_metadata.layout;
    const scenes = clipData.asset_metadata.scenes;
    const hasScenes = scenes && scenes.length > 0;

    let hasSpeakerLayout = false;
    if (hasScenes) {
      // If scenes exist, check if any scene has speaker layout
      hasSpeakerLayout = scenes.some(scene => isSpeakerLayout(scene.details.layout));
    } else {
      // If no scenes, fall back to clip layout
      hasSpeakerLayout = isSpeakerLayout(clipLayout);
    }

    if (!hasSpeakerLayout) {
      return;
    }

    // Check if there are warnings for layout
    const hasLayoutWarnings = clipCustomizerWarnings[CustomizerMenuType.Layout].showWarning;
    if (!hasLayoutWarnings) {
      return;
    }

    // All conditions met - trigger speaker identification popup
    hasTriggeredSpeakerIdentificationRef.current = true;
    window.analytics?.track('ContentLabEvent', {
      name: 'SpeakerIdentificationPopupAutoOpened',
      clipId: clipId
    });
    // Defer dispatch to next tick so all React effects in this render cycle
    // (including IdentifySpeakers re-registering its listener) complete first.
    const timeoutId = setTimeout(() => {
      EventBus.dispatch(CustomEvents.OpenSpeakersIdentification, {
        isInlineIdentification: true,
        layout: clipData.asset_metadata.layout
      });
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [clipData, featureFlags, clipCustomizerWarnings, clipId]);

  const onOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      closePopup();
    },
    [closePopup]
  );

  const onCaptionTranslation = useCallback(
    (selectedLanguage: LanguageOption | null) => {
      if (selectedLanguage) {
        onCaptionTranslationConfirm(selectedLanguage);
        closePopup();
      }
    },
    [closePopup, onCaptionTranslationConfirm]
  );

  const showLayoutPopup = useMemo(() => isMenuPopupOpened === CustomizerMenuType.Layout, [isMenuPopupOpened]);

  return (
    <div
      className={
        useNewRecordingsPlayer
          ? 'z-[55] flex h-full w-24 items-start justify-center self-center overflow-y-auto pt-24'
          : 'absolute right-2 z-[55] flex h-auto items-center self-center pl-8'
      }
      ref={popupMenu}
    >
      {isMenuPopupOpened && <div className="fixed right-0 top-0 size-full" onClick={onOverlayClick} />}
      <ActionButtons show={true} buttons={buttons} labelPosition="left" size="medium" />
      {showAIEditsTooltip && !isAIEditsHidden && !isAiEditsDisabled && (
        <AIEditsTooltip buttonRef={buttonRefs[CustomizerMenuType.AI]} onDismiss={() => setShowAIEditsTooltip(false)} />
      )}
      {isMenuPopupOpened && (
        <DynamicPopup
          triggerRef={buttonRefs[isMenuPopupOpened]}
          isVisible={!!isMenuPopupOpened}
          preferredPosition="right"
          offset={8}
          viewportPadding={16}
        >
          <Transition
            show={!!isMenuPopupOpened}
            enter="transition duration-150 ease-out"
            enterFrom="transform translate-x-2 scale-[0.98] opacity-0"
            enterTo="transform translate-x-0 scale-1 opacity-100"
            leave="transition duration-100 ease-out"
            leaveFrom="transform translate-x-0 scale-1 opacity-100"
            leaveTo="transform translate-x-2 scale-[0.98] opacity-0"
          >
            {isMenuPopupOpened === CustomizerMenuType.Captions && (
              <ClipCustomizerCaptionsPopup
                onTimeUpdate={onTimeUpdate}
                onEditCaptions={closePopup}
                isFullRecordingEdit={isFullRecordingEdit}
              />
            )}
            {isMenuPopupOpened === CustomizerMenuType.Templates && (
              <ClipCustomizerTemplatesPopup isFullRecordingEdit={isFullRecordingEdit} />
            )}
            {showLayoutPopup ? (
              <ClipCustomizerLayoutPopupV2 warnings={clipCustomizerWarnings[CustomizerMenuType.Layout].warnings} />
            ) : null}
            {isMenuPopupOpened === CustomizerMenuType.Background && <ClipCustomizerBackgroundPopup />}
            {isMenuPopupOpened === CustomizerMenuType.Speakers && <ClipCustomizerSpeakersPopup />}
            {isMenuPopupOpened === CustomizerMenuType.Translation && (
              <CaptionLanguageSelectorPopup
                closePopup={closePopup}
                onCaptionTranslation={onCaptionTranslation}
                clipData={clipData}
              />
            )}
            {isMenuPopupOpened === CustomizerMenuType.Images && <ClipCustomizerImagesPopup closePopup={closePopup} />}
            {isMenuPopupOpened === CustomizerMenuType.Videos && <ClipCustomizerVideoPopup closePopup={closePopup} />}
            {isMenuPopupOpened === CustomizerMenuType.Audio && <ClipCustomizerAudioPopup closePopup={closePopup} />}
          </Transition>
        </DynamicPopup>
      )}

      <UnsupportedLanguageWarningModal isOpen={isWarningModalOpen} onClose={closeWarningModal} />
    </div>
  );
}

export default memo(ClipCustomizerMenu);
